// Client-side PDF receipt for the denunciante (INT-06).
//
// Generated entirely in the browser via jspdf — never traverses the server.
// This is the *only* artifact carrying the cleartext protocolo + passphrase,
// and the user is solely responsible for storing it. We deliberately keep the
// document plain (no logo, no QR code) so it can be printed on receipt-style
// thermal paper or stored as a screenshot without losing legibility.
//
// Design constraints:
//   - Português, A4 portrait. Header + Protocolo + Passphrase + caseId in
//     monospace + a non-retaliation footer with the /status URL.
//   - No PII placeholders, no IP, no timestamp inside the PDF (intentional —
//     anything that could correlate this PDF back to a specific submission
//     event is verboten under our anonymity model).
//
// Layout (Opção C, 2026-06-23): credenciais EMPILHADAS — cada rótulo numa
// linha e o valor full-width na(s) linha(s) abaixo. NÃO há coluna de valor
// fixa: isso elimina por construção a colisão rótulo↔valor (o "Identificador
// do caso:" longo invadia o valor em x=65) e dá à frase-senha a largura cheia
// para quebrar sem encostar no rótulo seguinte. A geometria fica numa função
// pura (`computeReceiptLayout`) para ser verificável em teste.

import { jsPDF } from 'jspdf';

export interface ReceiptOptions {
  /** URL embedded in the footer — defaults to "/status" hash route. */
  statusUrl?: string;
}

/** Margens da página (mm). Largura útil = RIGHT - LEFT. A4 = 210mm largura. */
export const LEFT_MARGIN_MM = 20;
export const RIGHT_MARGIN_MM = 190;
const USABLE_WIDTH_MM = RIGHT_MARGIN_MM - LEFT_MARGIN_MM; // 170mm

/** Um run de texto resolvido: o que desenhar e onde. Saída da função de layout. */
export interface TextRun {
  text: string;
  x: number;
  y: number;
  font: 'helvetica' | 'courier';
  style: 'normal' | 'bold' | 'italic';
  size: number; // pt
}

/** Altura de linha em mm para um tamanho de fonte em pt (fator 1.25, folgado). */
function lineHeightMm(sizePt: number): number {
  return sizePt * 0.352778 * 1.25;
}

/**
 * Layout puro do comprovante. Recebe um `doc` jsPDF usado APENAS para medir/
 * quebrar texto (`splitTextToSize`) — não desenha nada e não muta o conteúdo
 * do documento (só setFont/setFontSize transitórios para medição). Devolve a
 * lista ordenada de runs a renderizar.
 *
 * Modelo empilhado: todo valor é full-width na própria linha, abaixo do rótulo.
 * Como cada valor é quebrado em `USABLE_WIDTH_MM`, fica garantido por
 * construção que `x + largura <= RIGHT_MARGIN_MM` (sem colisão, sem estouro de
 * margem). Verificável em teste via `computeReceiptLayout` + getTextWidth.
 */
export function computeReceiptLayout(
  doc: jsPDF,
  protocolo: string,
  passphrase: string,
  caseId: string,
  options: ReceiptOptions = {},
): TextRun[] {
  const runs: TextRun[] = [];
  const x = LEFT_MARGIN_MM;

  // Header
  runs.push({
    text: 'Canal de Denúncias — Contato Facilites',
    x,
    y: 25,
    font: 'helvetica',
    style: 'bold',
    size: 18,
  });
  runs.push({
    text: 'Comprovante de protocolo. Guarde este documento em local seguro:',
    x,
    y: 34,
    font: 'helvetica',
    style: 'normal',
    size: 11,
  });
  runs.push({
    text: 'as informações abaixo NÃO podem ser recuperadas pela empresa.',
    x,
    y: 40,
    font: 'helvetica',
    style: 'normal',
    size: 11,
  });

  // Bloco de credenciais — EMPILHADO (rótulo em cima, valor full-width abaixo).
  const credentials: ReadonlyArray<{ label: string; value: string; valueSize: number }> = [
    { label: 'Protocolo:', value: protocolo, valueSize: 13 },
    { label: 'Frase-Senha:', value: passphrase, valueSize: 13 },
    { label: 'Identificador do caso:', value: caseId, valueSize: 11 },
  ];
  const LABEL_SIZE = 11;
  const LABEL_TO_VALUE_GAP = lineHeightMm(LABEL_SIZE); // rótulo → 1ª linha do valor
  const CREDENTIAL_GAP = 5; // mm entre o fim de uma credencial e o próximo rótulo

  let y = 56;
  for (const cred of credentials) {
    runs.push({ text: cred.label, x, y, font: 'courier', style: 'bold', size: LABEL_SIZE });
    y += LABEL_TO_VALUE_GAP;

    doc.setFont('courier', 'normal');
    doc.setFontSize(cred.valueSize);
    const lines = doc.splitTextToSize(cred.value, USABLE_WIDTH_MM) as string[];
    const vlh = lineHeightMm(cred.valueSize);
    for (const line of lines) {
      runs.push({ text: line, x, y, font: 'courier', style: 'normal', size: cred.valueSize });
      y += vlh;
    }
    y += CREDENTIAL_GAP;
  }

  // Instruções — começam dinamicamente abaixo das credenciais (nunca colidem,
  // por mais que a frase-senha tenha quebrado em várias linhas).
  const blurb = [
    'Use o Protocolo e a Frase-Senha para consultar o andamento da denúncia',
    'na página de status do canal. Não compartilhe estes dados com ninguém.',
    '',
    'Política de Não-Retaliação: a Contato Facilites garante proteção integral',
    'a denunciantes de boa-fé, conforme Lei 14.457/2022 e Código de Conduta.',
  ];
  y += 4;
  for (const line of blurb) {
    if (line !== '') {
      runs.push({ text: line, x, y, font: 'helvetica', style: 'normal', size: 10 });
    }
    y += 6;
  }

  // Footer
  const statusUrl = options.statusUrl ?? '/#status';
  runs.push({
    text: `Consultar status: ${statusUrl}`,
    x,
    y: 280,
    font: 'helvetica',
    style: 'italic',
    size: 9,
  });

  return runs;
}

/**
 * Builds a PDF Blob containing the protocolo + passphrase + caseId.
 * Mime type is `application/pdf`. The document size is ~2-3kB for typical
 * inputs (single page, a few text runs).
 */
export function generateReceiptPdf(
  protocolo: string,
  passphrase: string,
  caseId: string,
  options: ReceiptOptions = {},
): Blob {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  // LT-10 — o jsPDF grava `/CreationDate` com data, hora AO SEGUNDO e o OFFSET DE
  // FUSO da máquina. Este é o único artefato que sai do canal na mão do
  // denunciante: ele pode anexar, enviar, sincronizar em nuvem. Metadado temporal
  // aqui é da mesma classe do LT-01 — cruzado com ponto/crachá/câmera, estreita o
  // conjunto de suspeitos. E o fuso é dimensão extra: separa quem estava fora do
  // fuso padrão num quadro de 1.300 pessoas.
  //
  // Fixamos numa constante em UTC. Não dá para OMITIR o campo (o jsPDF sempre o
  // escreve), então o neutralizamos: epoch 0 é obviamente sintético — um perito
  // lê como "sem data declarada", não como data real.
  //
  // ⚠️ TEM de ser a STRING, não `new Date(0)`. O `convertDateToPDFDate` do jsPDF
  // (dist/jspdf.node.js:1450) serializa em hora LOCAL e cola
  // `getTimezoneOffset()` — passar um Date fixo ainda entrega o fuso da máquina
  // (`D:19691231200000-04'00'`). O offset também não pode ser omitido nem virar
  // `Z`: o regex de validação (linha 1491) exige `[+-]HH'mm'`. `+00'00'` é a
  // única forma constante e sem informação de localização.
  doc.setCreationDate("D:19700101000000+00'00'");

  const runs = computeReceiptLayout(doc, protocolo, passphrase, caseId, options);
  for (const run of runs) {
    doc.setFont(run.font, run.style);
    doc.setFontSize(run.size);
    doc.text(run.text, run.x, run.y);
  }

  // jspdf's `output('blob')` returns a real Blob in the browser AND in
  // jsdom environments where Blob is polyfilled.
  const blob = doc.output('blob');
  // Guarantee MIME type for downstream `URL.createObjectURL` callers.
  if (blob.type !== 'application/pdf') {
    return new Blob([blob], { type: 'application/pdf' });
  }
  return blob;
}
